/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  DirectoryPersonGroup,
  DirectoryPersonGroup$inboundSchema,
} from "./directorypersongroup.js";

/**
 * How the profile relates to Speakeasy accounts. member: linked to, or the only email match of, a member of this organization. account_not_member: an account exists but is not a member here. not_signed_in: no matching account. ambiguous_email: the email matches more than one profile or account, so it is never matched.
 */
export const DirectoryPersonState = {
  Member: "member",
  AccountNotMember: "account_not_member",
  NotSignedIn: "not_signed_in",
  AmbiguousEmail: "ambiguous_email",
} as const;
/**
 * How the profile relates to Speakeasy accounts. member: linked to, or the only email match of, a member of this organization. account_not_member: an account exists but is not a member here. not_signed_in: no matching account. ambiguous_email: the email matches more than one profile or account, so it is never matched.
 */
export type DirectoryPersonState = OpenEnum<typeof DirectoryPersonState>;

export type DirectoryPerson = {
  /**
   * Email the directory reports for the profile. Empty when the directory reports none.
   */
  email: string;
  /**
   * First name the directory reports, when it reports one.
   */
  firstName?: string | undefined;
  /**
   * Live directory groups the profile belongs to.
   */
  groups: Array<DirectoryPersonGroup>;
  /**
   * Directory profile ID.
   */
  id: string;
  /**
   * Last name the directory reports, when it reports one.
   */
  lastName?: string | undefined;
  /**
   * How the profile relates to Speakeasy accounts. member: linked to, or the only email match of, a member of this organization. account_not_member: an account exists but is not a member here. not_signed_in: no matching account. ambiguous_email: the email matches more than one profile or account, so it is never matched.
   */
  state: DirectoryPersonState;
  /**
   * The matched Speakeasy user ID. Present only for member and account_not_member.
   */
  userId?: string | undefined;
};

/** @internal */
export const DirectoryPersonState$inboundSchema: z.ZodMiniType<
  DirectoryPersonState,
  unknown
> = openEnums.inboundSchema(DirectoryPersonState);

/** @internal */
export const DirectoryPerson$inboundSchema: z.ZodMiniType<
  DirectoryPerson,
  unknown
> = z.pipe(
  z.object({
    email: z.string(),
    first_name: z.optional(z.string()),
    groups: z.array(DirectoryPersonGroup$inboundSchema),
    id: z.string(),
    last_name: z.optional(z.string()),
    state: DirectoryPersonState$inboundSchema,
    user_id: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "first_name": "firstName",
      "last_name": "lastName",
      "user_id": "userId",
    });
  }),
);

export function directoryPersonFromJSON(
  jsonString: string,
): SafeParseResult<DirectoryPerson, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => DirectoryPerson$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'DirectoryPerson' from JSON`,
  );
}
